/* LogosLetterhead — Logos & Letterhead management tab
   Three upload panels: Header Logo · Email Logo · Letterhead PDF.
   Each panel shows a preview, last-uploaded date, upload button and remove button. */
import { useRef } from 'react';
import css from '../../../AdminPage.module.css';
import {
  useFirmLogo,
  useUploadFirmLogo,
  useDeleteFirmLogo,
  useFirmLetterhead,
  useUploadFirmLetterhead,
  useDeleteFirmLetterhead,
} from '../../../../../hooks/useAdmin';
import { useToast } from '../../../../../hooks/useToast';
import type { FirmLogo, FirmLetterhead } from '../../../../../types/admin';

type ApiErrorResponse = {
  response?: {
    data?: {
      error?: string;
    };
  };
};

function getApiErrorMessage(error: unknown, fallback: string): string {
  const maybeError = error as ApiErrorResponse;
  return maybeError.response?.data?.error || fallback;
}

/* ─── Logo panel ──────────────────────────────────────────────────────── */
interface LogoPanelProps {
  slot: 'header' | 'email';
  title: string;
  description: string;
}

function LogoPanel({ slot, title, description }: LogoPanelProps) {
  const { data: logo, isLoading } = useFirmLogo(slot);
  const upload = useUploadFirmLogo(slot);
  const remove = useDeleteFirmLogo(slot);
  const { showToast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const isPending = upload.isPending || remove.isPending;

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (fileRef.current) fileRef.current.value = '';
    const fd = new FormData();
    fd.append('logo', f);
    try {
      await upload.mutateAsync(fd);
      showToast(`${title} uploaded`, 'success');
    } catch (err: unknown) {
      showToast(getApiErrorMessage(err, `Failed to upload ${title.toLowerCase()}`), 'error');
    }
  }

  async function handleRemove() {
    try {
      await remove.mutateAsync();
      showToast(`${title} removed`, 'success');
    } catch (err: unknown) {
      showToast(getApiErrorMessage(err, `Failed to remove ${title.toLowerCase()}`), 'error');
    }
  }

  return (
    <div data-testid={`panel-${slot}-logo`} style={{ marginBottom: 32 }}>
      <h4 style={{ marginBottom: 4 }}>{title}</h4>
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-secondary)',
          marginBottom: 12,
        }}
      >
        {description}
      </p>

      {isLoading && (
        <div className={css.spinner}>
          <div className={css.spinnerDot} />
          <div className={css.spinnerDot} />
          <div className={css.spinnerDot} />
        </div>
      )}

      {!isLoading && logo && (
        <div style={{ marginBottom: 12 }}>
          <img
            data-testid={`img-logo-${slot}`}
            src={(logo as FirmLogo).url}
            alt={title}
            style={{
              maxHeight: 80,
              maxWidth: 320,
              border: '1px solid var(--border-primary)',
              borderRadius: 'var(--radius-sm)',
              padding: 4,
            }}
          />
          {logo.uploaded_at && (
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-tertiary)',
                marginTop: 4,
              }}
            >
              Uploaded: {new Date(logo.uploaded_at).toLocaleDateString('en-GB')}
            </div>
          )}
        </div>
      )}

      {!isLoading && !logo && (
        <p
          style={{
            color: 'var(--text-tertiary)',
            marginBottom: 12,
            fontSize: 'var(--font-size-sm)',
          }}
        >
          No logo uploaded yet
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          data-testid={`btn-upload-${slot}`}
          className={css.btnPrimary}
          onClick={() => fileRef.current?.click()}
          disabled={isPending}
        >
          {logo ? 'Replace' : 'Upload'} logo
        </button>

        {logo && (
          <button
            data-testid={`btn-remove-${slot}`}
            className={css.btnSmall}
            onClick={handleRemove}
            disabled={isPending}
            style={{ color: 'var(--error-600)' }}
          >
            Remove
          </button>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          style={{ display: 'none' }}
          data-testid={`input-${slot}-logo`}
        />
      </div>
    </div>
  );
}

/* ─── Letterhead panel ────────────────────────────────────────────────── */
function LetterheadPanel() {
  const { data: letterhead, isLoading } = useFirmLetterhead();
  const upload = useUploadFirmLetterhead();
  const remove = useDeleteFirmLetterhead();
  const { showToast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const isPending = upload.isPending || remove.isPending;

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (fileRef.current) fileRef.current.value = '';
    const fd = new FormData();
    fd.append('letterhead', f);
    try {
      await upload.mutateAsync(fd);
      showToast('Letterhead uploaded', 'success');
    } catch (err: unknown) {
      showToast(getApiErrorMessage(err, 'Failed to upload letterhead'), 'error');
    }
  }

  async function handleRemove() {
    try {
      await remove.mutateAsync();
      showToast('Letterhead removed', 'success');
    } catch (err: unknown) {
      showToast(getApiErrorMessage(err, 'Failed to remove letterhead'), 'error');
    }
  }

  return (
    <div data-testid="panel-letterhead">
      <h4 style={{ marginBottom: 4 }}>Letterhead PDF</h4>
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-secondary)',
          marginBottom: 12,
        }}
      >
        The PDF letterhead used in all generated letters. Replaces the previous BLP letterhead
        management.
      </p>

      {isLoading && (
        <div className={css.spinner}>
          <div className={css.spinnerDot} />
          <div className={css.spinnerDot} />
          <div className={css.spinnerDot} />
        </div>
      )}

      {!isLoading && letterhead && (
        <div style={{ marginBottom: 12 }}>
          <a
            data-testid="link-letterhead"
            href={(letterhead as FirmLetterhead).url}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}
          >
            📄 View current letterhead
          </a>
          {letterhead.uploaded_at && (
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-tertiary)',
                marginTop: 4,
              }}
            >
              Uploaded: {new Date(letterhead.uploaded_at).toLocaleDateString('en-GB')}
            </div>
          )}
        </div>
      )}

      {!isLoading && !letterhead && (
        <p
          style={{
            color: 'var(--text-tertiary)',
            marginBottom: 12,
            fontSize: 'var(--font-size-sm)',
          }}
        >
          No letterhead uploaded yet
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          data-testid="btn-upload-letterhead"
          className={css.btnPrimary}
          onClick={() => fileRef.current?.click()}
          disabled={isPending}
        >
          {letterhead ? 'Replace' : 'Upload'} letterhead
        </button>

        {letterhead && (
          <button
            data-testid="btn-remove-letterhead"
            className={css.btnSmall}
            onClick={handleRemove}
            disabled={isPending}
            style={{ color: 'var(--error-600)' }}
          >
            Remove
          </button>
        )}

        <input
          ref={fileRef}
          type="file"
          accept=".pdf"
          onChange={handleFile}
          style={{ display: 'none' }}
          data-testid="input-letterhead"
        />
      </div>
    </div>
  );
}

/* ─── Main tab component ─────────────────────────────────────────────── */
export default function LogosLetterhead() {
  return (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <h2 className={css.sectionTitle}>Logos &amp; Letterhead</h2>
        <p className={css.sectionSubtitle}>
          Upload your firm&apos;s logos and letterhead PDF. Changes take effect immediately.
        </p>
      </div>

      <LogoPanel
        slot="header"
        title="Header Logo"
        description="Displayed in the app header. Recommended: PNG or SVG, transparent background, max 320 × 80 px."
      />

      <hr
        style={{
          border: 'none',
          borderTop: '1px solid var(--border-primary)',
          margin: '8px 0 24px',
        }}
      />

      <LogoPanel
        slot="email"
        title="Email Logo"
        description="Embedded at the top of outbound emails. Recommended: PNG, max 600 × 200 px."
      />

      <hr
        style={{
          border: 'none',
          borderTop: '1px solid var(--border-primary)',
          margin: '8px 0 24px',
        }}
      />

      <LetterheadPanel />
    </div>
  );
}
